import { setIn, getIn, removeIn } from "immutable";
import { createSlice, current, original } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  AllComponents,
  WebpageComponentRelation,
} from "../../../../components/_default/interfaces/base";
import { CSSProperties } from "react";
import type { Editor } from "../editor/editorTypes";
import {
  getCanonicalComponentKey,
  resolveComponentMapKey,
  withComponentAliases,
  normalizeStructureForClient,
} from "./structureIdentity";
import { computeStructureReorderPlan } from "./structureReorderPlan";

// const initialState: Set<AllComponents[]> = Set([]) // Was unable to use immutable Set. Too much problems
// const initialState: AllComponents[] = [] // Was unable to use immutable Set. Too much problems

export interface StructureMap {
  webpageComponents: Array<string>;
  components: {
    [key: string]: AllComponents;
  };
}
const initialState: StructureMap = {
  webpageComponents: [],
  components: {},
};

function resequenceRelations(parent: AllComponents | undefined): void {
  if (!parent || !Array.isArray(parent.childWebpageComponentRelations)) {
    return;
  }
  parent.childWebpageComponentRelations = parent.childWebpageComponentRelations
    .filter((relation): relation is WebpageComponentRelation => !!relation)
    .map((relation, index) => ({
      ...relation,
      sort: index + 1,
    }));
}

export const structureSlice = createSlice({
  name: "structure",
  initialState,
  reducers: {
    UPDATE_STRUCTURE: (state, action: PayloadAction<StructureMap>) => {
      // Ensure we always have valid components objects
      const updatedComponents = {
        ...(state.components || {}),
        ...(action.payload.components || {}),
      };
      let updatedWebpageComponents = [...(state.webpageComponents ?? [])];
      (action.payload.webpageComponents ?? []).map((webpageComponentId) => {
        const rootKey =
          resolveComponentMapKey(updatedComponents, webpageComponentId) ??
          String(webpageComponentId);
        if (
          updatedWebpageComponents.find((c) => c === rootKey) == null
        ) {
          updatedWebpageComponents = [
            ...updatedWebpageComponents,
            rootKey,
          ];
        }
        // else {
        //   updatedWebpageComponents = updatedWebpageComponents.map((c) => {
        //     if (c.id === component.id) {
        //       return { ...c, ...component }
        //     } else {
        //       return c
        //     }
        //   })
        // }
      });
      updatedWebpageComponents = updatedWebpageComponents.sort((a, b) => {
        const aComponent = updatedComponents[a];
        const bComponent = updatedComponents[b];
        if (!aComponent || !bComponent) {
          return 0;
        }
        if (aComponent.sort_order > bComponent.sort_order) {
          return 1;
        }
        if (aComponent.sort_order < bComponent.sort_order) {
          return -1;
        }
        return 0;
      });

      return normalizeStructureForClient({
        components: updatedComponents,
        webpageComponents: [...updatedWebpageComponents],
      });
    },
    SET_STRUCTURE: (state, action: PayloadAction<StructureMap>) => {
      // Ensure components is always defined, even if API returns undefined/null
      return normalizeStructureForClient({
        webpageComponents: (action.payload.webpageComponents || []).map((x) =>
          String(x)
        ),
        components: action.payload.components || {},
      });
    },

    UPDATE_STRUCTURE_WEBPAGECOMPONENTS_ORDER: (
      state,
      action: PayloadAction<{
        draggedComponentId: string | number;
        destinationId: string | number;
        position: string;
      }>
    ) => {
      const { draggedComponentId, destinationId, position } = action.payload;
      const webpageComponents = [...state.webpageComponents];
      const draggedKey =
        resolveComponentMapKey(state.components, draggedComponentId) ??
        String(draggedComponentId);
      const destinationKey =
        resolveComponentMapKey(state.components, destinationId) ??
        String(destinationId);

      const draggedIndex = webpageComponents.indexOf(draggedKey);
      const destinationIndex = webpageComponents.indexOf(destinationKey);
      if (draggedIndex === -1 || destinationIndex === -1) {
        return state;
      }

      if (position === "top") {
        // Remove the dragged component from its original position
        webpageComponents.splice(draggedIndex, 1);

        // Insert the dragged component one place ahead of the destination index
        if (draggedIndex < destinationIndex) {
          // If the dragged index is before the destination index, adjust the destination index accordingly
          webpageComponents.splice(
            destinationIndex - 1,
            0,
            draggedKey
          );
        } else {
          webpageComponents.splice(
            destinationIndex,
            0,
            draggedKey
          );
        }
      } else if (position === "bottom") {
        // Remove the dragged component from its original position
        webpageComponents.splice(draggedIndex, 1);

        // Insert the dragged component after the destination index
        if (draggedIndex > destinationIndex) {
          // If the dragged index is greater than the destination index, adjust the destination index accordingly
          webpageComponents.splice(
            destinationIndex + 1,
            0,
            draggedKey
          );
        } else {
          webpageComponents.splice(
            destinationIndex,
            0,
            draggedKey
          );
        }
      }

      return {
        ...state,
        webpageComponents,
      };
    },

    UPDATE_STRUCTURE_COMPONENT_ORDER: (
      state,
      action: PayloadAction<{
        parent_id: string | number;
        draggedComponentId: string | number;
        destinationId: string | number;
        position: string;
        section: boolean;
      }>
    ) => {
      const plan = computeStructureReorderPlan(state.components, action.payload);
      if (!plan.ok) {
        return;
      }

      const sourceParent = state.components[plan.sourceParentKey];
      const targetParent = state.components[plan.targetParentKey];
      const draggedComponent = state.components[plan.draggedKey];
      if (!sourceParent || !targetParent || !draggedComponent) {
        return;
      }

      sourceParent.childWebpageComponentRelations.splice(plan.draggedIndex, 1);

      const targetParentUuid =
        (targetParent as { uuid?: string | null }).uuid?.trim() || null;
      draggedComponent.parent_id = targetParent.id ?? null;
      (draggedComponent as { parent_uuid?: string | null }).parent_uuid = targetParentUuid;
      plan.sourceRelation.parent_id = targetParent.id ?? plan.sourceRelation.parent_id ?? null;
      (plan.sourceRelation as { parent_uuid?: string | null }).parent_uuid = targetParentUuid;
      plan.sourceRelation.child_id = draggedComponent.id ?? plan.sourceRelation.child_id ?? null;
      (plan.sourceRelation as { child_uuid?: string | null }).child_uuid =
        draggedComponent.uuid?.trim() ||
        (plan.sourceRelation as { child_uuid?: string | null }).child_uuid ||
        null;

      targetParent.childWebpageComponentRelations.splice(
        plan.insertIndex,
        0,
        plan.sourceRelation
      );
      resequenceRelations(sourceParent);
      resequenceRelations(targetParent);
    },

    UPDATE_STRUCTURE_COMPONENT: (
      state,
      action: PayloadAction<{
        id: string | number;
        key: any;
        value: any;
      }>
    ) => {
      const mapKey =
        resolveComponentMapKey(state.components, action.payload.id) ??
        String(action.payload.id);
      let updateComponent = state.components[mapKey];
      if (!updateComponent) {
        return state;
      }

      let fullPath = action.payload.key.split(".");

      var oldComponentValue = getIn(updateComponent, fullPath) as {};
      var payloadValue = action.payload.value;
      var newValue = payloadValue;
      while (oldComponentValue === undefined && fullPath.length > 0) {
        // work out until find existing object key in state
        let pop = fullPath.pop();
        let tmp: any = {};
        tmp[pop] = payloadValue;
        payloadValue = tmp;

        oldComponentValue = getIn(updateComponent, fullPath) as {};
        var newValue = {
          ...oldComponentValue,
          ...payloadValue,
        };
      }
      // Return object with changes
      updateComponent = setIn(updateComponent, fullPath, newValue);
      const canonical =
        getCanonicalComponentKey(updateComponent, mapKey) ?? mapKey;

      return {
        ...state,
        components: {
          ...state.components,
          [canonical]: updateComponent,
        },
      };
    },
    ADD_NEW_COMPONENTS: (
      state,
      action: PayloadAction<{ [key: string]: AllComponents }>
    ) => {
      let updatedParentComponents: any = {};
      let updatedWebpageComponents: string[] = [];
      Object.entries(action.payload).forEach(([key, value]) => {
        const canonicalKey = getCanonicalComponentKey(value, key) ?? key;
        if (value.parent_id) {
          const parentMapKey =
            resolveComponentMapKey(state.components, value.parent_id) ??
            resolveComponentMapKey(state.components, (value as any).parent_uuid ?? null);
          let updateParentComponent = parentMapKey
            ? state.components[parentMapKey]
            : undefined;
          if (updateParentComponent) {
            const updatedRelations = [
              ...updateParentComponent.childWebpageComponentRelations,
              {
                id: 0,
                parent_id: value.parent_id,
                child_id: value.id,
                sort: value.sort_order,
                created_at: null,
                updated_at: null,
              } as WebpageComponentRelation,
            ].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

            const normalizedParentKey =
              getCanonicalComponentKey(updateParentComponent) ??
              String(updateParentComponent.id);
            updatedParentComponents[normalizedParentKey] = {
              ...updateParentComponent,
              childWebpageComponentRelations: updatedRelations,
            };
          } else {
            // If parent cannot be resolved in map, keep component reachable as root.
            updatedWebpageComponents.push(canonicalKey);
          }
        } else updatedWebpageComponents.push(canonicalKey);
      });

      const mergedComponents: StructureMap["components"] = {
        ...state.components,
        ...updatedParentComponents,
        ...withComponentAliases(action.payload),
      };

      const mergedWebpageComponents = [
        ...state.webpageComponents,
        ...updatedWebpageComponents,
      ];
      const uniqueWebpageComponents = Array.from(
        new Set(mergedWebpageComponents)
      ).sort((a, b) => {
        const aComponent = mergedComponents[a];
        const bComponent = mergedComponents[b];
        if (!aComponent || !bComponent) {
          return 0;
        }
        return (aComponent.sort_order ?? 0) - (bComponent.sort_order ?? 0);
      });

      return {
        ...state,
        components: {
          ...mergedComponents,
        },
        webpageComponents: uniqueWebpageComponents,
      };
    },
    ADD_NEW_COMPONENT: (state, action: PayloadAction<AllComponents>) => {
      const componentKey =
        getCanonicalComponentKey(action.payload) ?? String(action.payload.id);
      const payloadParentUuid =
        typeof (action.payload as any).parent_uuid === "string"
          ? (action.payload as any).parent_uuid.trim()
          : "";
      const hasParent =
        (action.payload.parent_id != null && action.payload.parent_id !== 0) ||
        payloadParentUuid !== "";
      if (hasParent) {
        const parentMapKey =
          resolveComponentMapKey(state.components, action.payload.parent_id) ??
          resolveComponentMapKey(
            state.components,
            payloadParentUuid || (action.payload as any).parent_uuid || null
          );
        let updateParentComponent = parentMapKey
          ? state.components[parentMapKey]
          : undefined;
        if (!updateParentComponent) {
          const updatedComponents: StructureMap["components"] = {
            ...state.components,
            [componentKey]: action.payload,
          };
          return {
            ...state,
            components: updatedComponents,
            webpageComponents: Array.from(
              new Set([...state.webpageComponents, componentKey])
            ),
          };
        }
        updateParentComponent = {
          ...updateParentComponent,
          childWebpageComponentRelations: [
            ...updateParentComponent.childWebpageComponentRelations,
            {
              id: 0,
              parent_id:
                updateParentComponent.id != null
                  ? updateParentComponent.id
                  : (action.payload.parent_id as number) ?? 0,
              child_id: action.payload.id,
              sort: action.payload.sort_order,
              created_at: null,
              updated_at: null,
              parent_uuid: updateParentComponent.uuid?.trim() ?? null,
              child_uuid: action.payload.uuid?.trim() ?? null,
            } as WebpageComponentRelation,
          ],
        };
        // let updatedComponents = state.components.map(c => {
        //   if (c.id === action.payload.parent_id) {
        //     // Create a *new* object with changes
        //     return {
        //       ...c, childWebpageComponentRelations: [
        //         ...c.childWebpageComponentRelations,
        //         {
        //           id: 0,
        //           parent_id: action.payload.parent_id,
        //           child_id: action.payload.id,
        //           sort: action.payload.sort_order,
        //           created_at: null,
        //           updated_at: null,
        //         } as WebpageComponentRelation
        //       ]
        //     };
        //   } else {
        //     // No changes
        //     return c;
        //   }
        // })
        return {
          ...state,
          components: {
            ...state.components,
            [componentKey]: action.payload,
            [getCanonicalComponentKey(updateParentComponent) ?? String(updateParentComponent.id)]:
              updateParentComponent,
          },
        };
      } else {
        const updatedComponents: StructureMap["components"] = {
          ...state.components,
          [componentKey]: action.payload,
        };
        return {
          ...state,
          components: updatedComponents,
          webpageComponents: [
            ...state.webpageComponents,
            componentKey,
          ].sort((a, b) => {
            const aComponent = updatedComponents[a];
            const bComponent = updatedComponents[b];
            if (!aComponent || !bComponent) {
              return 0;
            }
            if (aComponent.sort_order > bComponent.sort_order) {
              return 1;
            }
            if (aComponent.sort_order < bComponent.sort_order) {
              return -1;
            }
            return 0;
          }),
        };
      }
    },
    ADD_CHILD_TO_PARENT: (
      state,
      action: PayloadAction<{
        parent_id: number;
        child_id: number;
        after: number;
      }>
    ) => {
      if (action.payload.parent_id) {
        const parentKey =
          resolveComponentMapKey(state.components, action.payload.parent_id) ??
          String(action.payload.parent_id);
        let updateParentComponent = state.components[parentKey];
        if (!updateParentComponent) {
          return state;
        }
        updateParentComponent = {
          ...updateParentComponent,
          childWebpageComponentRelations: [
            ...updateParentComponent.childWebpageComponentRelations,
            {
              id: 0,
              parent_id: action.payload.parent_id,
              child_id: action.payload.child_id,
              sort: action.payload.after,
              created_at: null,
              updated_at: null,
            } as WebpageComponentRelation,
          ],
        };

        return {
          ...state,
          components: {
            ...state.components,
            [getCanonicalComponentKey(updateParentComponent) ?? parentKey]:
              updateParentComponent,
          },
        };
      }
    },
    REMOVE_CHILD_FROM_PARENT: (
      state,
      action: PayloadAction<{
        parent_id: number | string;
        child_id?: number | string | null;
        child_uuid?: string | null;
      }>
    ) => {
      if (action.payload.parent_id) {
        const parentKey =
          resolveComponentMapKey(state.components, action.payload.parent_id) ??
          String(action.payload.parent_id);
        let updateParentComponent = state.components[parentKey];
        if (!updateParentComponent) {
          return state;
        }
        const index =
          updateParentComponent.childWebpageComponentRelations.findIndex(
            (childWebpageComponentRelation) =>
              (action.payload.child_id != null &&
                (resolveComponentMapKey(state.components, childWebpageComponentRelation.child_id) ??
                  String(childWebpageComponentRelation.child_id)) ===
                  (resolveComponentMapKey(state.components, action.payload.child_id) ??
                    String(action.payload.child_id))) ||
              (action.payload.child_uuid != null &&
                (childWebpageComponentRelation as { child_uuid?: string | null }).child_uuid?.trim() ===
                  action.payload.child_uuid.trim())
          );
        if (index !== -1) {
          updateParentComponent = {
            ...updateParentComponent,
            childWebpageComponentRelations: [
              ...updateParentComponent.childWebpageComponentRelations.slice(
                0,
                index
              ),
              ...updateParentComponent.childWebpageComponentRelations.slice(
                index + 1
              ),
            ],
          };
        }

        return {
          ...state,
          components: {
            ...state.components,
            [getCanonicalComponentKey(updateParentComponent) ?? parentKey]:
              updateParentComponent,
          },
        };
      }
    },
    DELETE_COMPONENT: (
      state,
      action: PayloadAction<{
        id: string | number;
        editor: Editor | null;
      }>
    ) => {
      const component_parent_id = action.payload.editor?.component_parent_id;
      const targetKey =
        resolveComponentMapKey(state.components, action.payload.id) ??
        String(action.payload.id);
      const targetComponent = state.components[targetKey];
      if (!targetComponent) {
        return state;
      }
      const targetCanonical =
        getCanonicalComponentKey(targetComponent, targetKey) ?? targetKey;
      // Remove Component. And remove from childWebpageComponentRelations
      var updatedComponents = { ...state.components };
      delete updatedComponents[targetKey];
      if (targetComponent.uuid?.trim()) {
        delete updatedComponents[targetComponent.uuid.trim()];
      }
      delete updatedComponents[String(targetComponent.id)];
      const parentMatchKey =
        component_parent_id != null
          ? resolveComponentMapKey(updatedComponents, component_parent_id)
          : null;
      const parentMatchCanonical =
        parentMatchKey != null
          ? getCanonicalComponentKey(updatedComponents[parentMatchKey], parentMatchKey)
          : null;

      Object.keys(updatedComponents).map((key) => {
        let c: AllComponents = updatedComponents[key];
        if (component_parent_id) {
          const candidateCanonical = getCanonicalComponentKey(c, key);
          if (
            parentMatchCanonical &&
            candidateCanonical &&
            candidateCanonical === parentMatchCanonical
          ) {
            if (!c.childWebpageComponentRelations) {
              return c;
            }
            const childWebpageComponentRelations =
              c.childWebpageComponentRelations.filter(
                (cr) =>
                  cr.child_id !== targetComponent.id &&
                  (cr as { child_uuid?: string | null }).child_uuid !==
                    targetComponent.uuid
              );
            updatedComponents[key] = {
              ...c,
              childWebpageComponentRelations: childWebpageComponentRelations,
            };
            return {
              ...c,
              childWebpageComponentRelations: childWebpageComponentRelations,
            };
          }
          return c;
        } else {
          if (!c.childWebpageComponentRelations) {
            return c;
          }
          const childWebpageComponentRelations =
            c.childWebpageComponentRelations.filter(
              (cr) =>
                cr.child_id !== targetComponent.id &&
                (cr as { child_uuid?: string | null }).child_uuid !==
                  targetComponent.uuid
            );
          updatedComponents[key] = {
            ...c,
            childWebpageComponentRelations: childWebpageComponentRelations,
          };
          return {
            ...c,
            childWebpageComponentRelations: childWebpageComponentRelations,
          };
        }
      });
      var updatedWebpageComponents = [...state.webpageComponents];
      if (!component_parent_id) {
        updatedWebpageComponents = updatedWebpageComponents.filter(
          (webpageComponentId) => {
            return webpageComponentId !== targetCanonical;
          }
        );
      }
      return {
        ...state,
        components: {
          ...updatedComponents,
        },
        webpageComponents: [...updatedWebpageComponents],
      };
    },
    DELETE_COMPONENT_STYLE: (
      state,
      action: PayloadAction<{
        id: string | number;
        styles: string[];
      }>
    ) => {
      const mapKey =
        resolveComponentMapKey(state.components, action.payload.id) ??
        String(action.payload.id);
      return setIn(
        state,
        ["components", mapKey, "attributes", "styles"],
        Object.fromEntries(
          Object.entries(
            getIn(state, [
              "components",
              mapKey,
              "attributes",
              "styles",
            ]) as {}
          ).filter(([key]) => !action.payload.styles.includes(key))
        )
      );
    },
    UPDATE_COMPONENT_STYLE: (
      state,
      action: PayloadAction<{
        id: string | number;
        style: CSSProperties;
      }>
    ) => {
      const mapKey =
        resolveComponentMapKey(state.components, action.payload.id) ??
        String(action.payload.id);
      let updateComponent = { ...state.components[mapKey] };

      const fullPath = ["attributes", "styles"];

      const oldStyle = getIn(updateComponent, fullPath) as {};
      const newStyle = {
        ...oldStyle,
        ...action.payload.style,
      };
      // updateComponent = setIn(updateComponent, fullPath, newStyle)
      return setIn(
        state,
        ["components", mapKey, ...fullPath],
        newStyle
      );
      // return {
      //   ...state,
      //   components: {
      //     ...state.components,
      //     [updateComponent.id]: updateComponent
      //   }
      // }
    },

    UPDATE_COMPONENT_TEXT: (
      state,
      action: PayloadAction<{
        id: string | number;
        text: string;
      }>
    ) => {
      const mapKey =
        resolveComponentMapKey(state.components, action.payload.id) ??
        String(action.payload.id);
      let component = { ...state.components[mapKey] };
      const canonical = getCanonicalComponentKey(component, mapKey) ?? mapKey;

      component.attributes = {
        ...component.attributes,
        text: action.payload.text,
      };

      return {
        ...state,
        components: {
          ...state.components,
          [canonical]: component,
        },
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  SET_STRUCTURE,
  UPDATE_STRUCTURE,
  UPDATE_COMPONENT_TEXT,
  UPDATE_STRUCTURE_COMPONENT,
  UPDATE_COMPONENT_STYLE,
  UPDATE_STRUCTURE_COMPONENT_ORDER,
  UPDATE_STRUCTURE_WEBPAGECOMPONENTS_ORDER,
  ADD_NEW_COMPONENT,
  ADD_NEW_COMPONENTS,
  REMOVE_CHILD_FROM_PARENT,
  DELETE_COMPONENT,
  DELETE_COMPONENT_STYLE,
  ADD_CHILD_TO_PARENT,
} = structureSlice.actions;

export default structureSlice.reducer;
